import {
  PROPERTY_FEE_KEYS,
  type PropertyFeeFrequency,
  type PropertyFeeKey
} from "shared/types";
import { formatToCurrency } from "shared/utils";
import type { PropertyFee, PropertyFeesUpdate } from "types/landlord/address";

export const PROPERTY_FEE_LABEL: Record<PropertyFeeKey, string> = {
  pet_fee: "Pet fee",
  storage_fee: "Storage fee",
  amenity_fee: "Amenity fee",
  parking_fee: "Parking fee",
  trash_fee: "Trash fee",
  admin_fee: "Admin fee"
};

const PROPERTY_FEE_FREQUENCY_LABEL: Record<PropertyFeeFrequency, string> = {
  one_time_move_in: "One-time at move-in",
  one_time_application: "One-time at application",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually"
};

// Billed dropdown order.
export const PROPERTY_FEE_FREQUENCY_OPTIONS = (
  Object.keys(PROPERTY_FEE_FREQUENCY_LABEL) as PropertyFeeFrequency[]
).map((value) => ({ value, label: PROPERTY_FEE_FREQUENCY_LABEL[value] }));

// Billed defaults to Monthly the first time a fee gets an amount.
const DEFAULT_PROPERTY_FEE_FREQUENCY: PropertyFeeFrequency = "monthly";

const blankFee = (key: PropertyFeeKey): PropertyFee => ({
  key,
  amount_cents: null,
  frequency: null,
  manual_amount_cents: null,
  pms_amount_cents: null,
  pms_mapped: false,
  pms_synced: false
});

// All six fees in fixed order, whatever the API returned. A missing `fees` (or
// a missing key) reads as a blank, unmapped fee.
export const normalizePropertyFees = (
  fees: PropertyFee[] | undefined
): PropertyFee[] =>
  PROPERTY_FEE_KEYS.map(
    (key) => fees?.find((fee) => fee.key === key) ?? blankFee(key)
  );

// $0 and blank both mean "not charged".
export const isChargedAmount = (cents: number | null): cents is number =>
  cents != null && cents > 0;

export const formatFeeAmount = (cents: number): string =>
  formatToCurrency({ cents, currency: "USD" }, { minimumFractionDigits: 2 });

// Read view value, e.g. "$300.00 per pet · One-time at move-in". `null` when
// the fee isn't charged, so the row falls back to "-".
export const formatPropertyFee = (fee: PropertyFee): string | null => {
  if (!isChargedAmount(fee.amount_cents)) return null;

  const amount = `${formatFeeAmount(fee.amount_cents)}${
    fee.key === "pet_fee" ? " per pet" : ""
  }`;
  return fee.frequency
    ? `${amount} · ${PROPERTY_FEE_FREQUENCY_LABEL[fee.frequency]}`
    : amount;
};

// Edit state for one fee. `manual_amount_cents` is what the user typed; while
// synced the input shows the PMS value instead, and the typed amount is kept
// for when they switch back to Edit manually.
export interface PropertyFeeDraft {
  manual_amount_cents: number | null;
  frequency: PropertyFeeFrequency | null;
  pms_synced: boolean;
}

export type PropertyFeeDrafts = Record<PropertyFeeKey, PropertyFeeDraft>;

const isSynced = (fee: PropertyFee, draft: PropertyFeeDraft) =>
  fee.pms_mapped && draft.pms_synced;

// The amount the fee would charge with this draft: the PMS value while synced,
// otherwise the typed amount.
export const effectiveDraftAmount = (
  fee: PropertyFee,
  draft: PropertyFeeDraft
): number | null =>
  isSynced(fee, draft) ? fee.pms_amount_cents : draft.manual_amount_cents;

// No typed amount → no frequency; a new amount → Billed defaults to Monthly. A
// frequency the user already picked is kept, including while a synced fee's PMS
// value is blank, so switching to Edit manually brings it back.
export const applyFrequencyDefault = (
  fee: PropertyFee,
  draft: PropertyFeeDraft
): PropertyFeeDraft => {
  if (!isChargedAmount(effectiveDraftAmount(fee, draft)))
    return isSynced(fee, draft) ? draft : { ...draft, frequency: null };
  if (!draft.frequency)
    return { ...draft, frequency: DEFAULT_PROPERTY_FEE_FREQUENCY };
  return draft;
};

const toDraft = (fee: PropertyFee): PropertyFeeDraft => ({
  manual_amount_cents: fee.pms_mapped
    ? fee.manual_amount_cents
    : (fee.amount_cents ?? fee.manual_amount_cents),
  frequency: fee.frequency,
  pms_synced: fee.pms_mapped ? fee.pms_synced : false
});

// Drafts as saved on the server, before any defaulting.
const toSavedDrafts = (fees: PropertyFee[]): PropertyFeeDrafts =>
  Object.fromEntries(
    fees.map((fee) => [fee.key, toDraft(fee)])
  ) as PropertyFeeDrafts;

export const buildInitialFeeDrafts = (fees: PropertyFee[]): PropertyFeeDrafts =>
  Object.fromEntries(
    fees.map((fee) => [fee.key, applyFrequencyDefault(fee, toDraft(fee))])
  ) as PropertyFeeDrafts;

const sameDraft = (a: PropertyFeeDraft, b: PropertyFeeDraft) =>
  a.frequency === b.frequency &&
  a.pms_synced === b.pms_synced &&
  (a.manual_amount_cents ?? 0) === (b.manual_amount_cents ?? 0);

// PATCH `fees` body: only fees whose draft differs from what's saved. A synced
// fee doesn't send an amount (the server ignores it while synced); a $0 amount
// goes out as null, both meaning "not charged". `undefined` when nothing changed.
export const buildPropertyFeesPayload = (
  fees: PropertyFee[],
  drafts: PropertyFeeDrafts
): PropertyFeesUpdate | undefined => {
  const saved = toSavedDrafts(fees);
  const payload: PropertyFeesUpdate = {};

  fees.forEach((fee) => {
    const draft = drafts[fee.key];
    if (!draft || sameDraft(draft, saved[fee.key])) return;

    const synced = isSynced(fee, draft);
    payload[fee.key] = {
      ...(fee.pms_mapped ? { pms_synced: draft.pms_synced } : {}),
      ...(synced
        ? {}
        : {
            amount_cents: isChargedAmount(draft.manual_amount_cents)
              ? draft.manual_amount_cents
              : null
          }),
      frequency: draft.frequency
    };
  });

  return Object.keys(payload).length ? payload : undefined;
};
